// src/components/TableStopwatch.tsx
import { useState, useEffect, useRef } from "react";
import { StopwatchSession, Table } from "../../utilities/types"; // Make sure you have a type for your table
import { MoveIcon, PauseIcon, StartIcon, StopIcon } from "../../assets/icons";
import { useStopwatch } from "../../context/StopwatchContext";
// You will need to replace your old service calls with the new ones for syncing
// import { syncSession, changeTableName } from "../../services/api"; 

// --- Helper function to display time ---
const calculateDisplayTime = (session: StopwatchSession | undefined) => {
    if (!session) {
        return { hours: 0, minutes: 0, seconds: 0 };
    }

    let totalMs = session.elapsedTime;

    // If the timer is running, add the time that has passed since it was last started
    if (session.state === 'running' && session.lastStartTime) {
        const now = new Date();
        // console.log(now);

        // `new Date(session.lastStartTime)` correctly parses the UTC string
        // from the server into a Date object representing that point in time,
        // adjusted for the user's timezone.
        console.log(session.lastStartTime);
        const lastStart = new Date(session.lastStartTime);
        console.log(lastStart);

        // `.getTime()` returns milliseconds since the epoch, which is timezone-independent.
        // The subtraction is always accurate.
        totalMs += now.getTime() - lastStart.getTime();
    }

    const totalSeconds = Math.floor(totalMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return { hours, minutes, seconds };
};

const padWithLeadingZeros = (num: number, length: number) => {
    return String(num).padStart(length, '0');
}

// --- Main Component ---
interface Props {
    table: Table;
    allTables: Table[]; // Renamed for clarity
    // You'll need a way to trigger a sync, which can be passed down or handled in a global context
    // syncAllSessions: () => void;
}

const V2Stopwatch = ({ table, allTables }: Props) => {
    // The hook now manages ALL sessions, we find the one for THIS table
    const { sessions, createSession, start, pause, resume, stop, moveTable } = useStopwatch();
    const activeSession = sessions.find(s => s.tableId === table.id && s.state !== 'stopped');

    // State for the live-updating timer display
    const [displayTime, setDisplayTime] = useState(calculateDisplayTime(activeSession));

    // This useEffect is the "ticking" engine for the clock display.
    useEffect(() => {
        console.log("Active session changed:", activeSession);
        let timerId: number | undefined = undefined;

        // If the session for this table is running, start an interval.
        if (activeSession?.state === 'running') {
            timerId = window.setInterval(() => {
                // Every second, recalculate and update the display time.
                setDisplayTime(calculateDisplayTime(activeSession));
            }, 1000);
            console.log(calculateDisplayTime(activeSession));
        } else {
            // If the session is paused, stopped, or doesn't exist,
            // just calculate the final time once and set it.
            setDisplayTime(calculateDisplayTime(activeSession));
        }

        // IMPORTANT: Cleanup function.
        // This stops the interval when the component unmounts or when the activeSession changes.
        // This prevents memory leaks and multiple timers running at once.
        return () => {
            if (timerId) {
                clearInterval(timerId);
            }
        };
    }, [activeSession]); // This effect MUST re-run whenever the activeSession object changes.


    // UI state remains in the component
    const [isLoading, setIsLoading] = useState(false);
    const [editName, setEditName] = useState(false);
    const nameChangeRef = useRef<HTMLInputElement>(null);

    // --- Event Handlers ---
    const handleStartPauseResume = () => {
        if (!activeSession) {
            // Create and then immediately start a new session
            console.log("Creating new session for table:", table.name);
            const newSession = createSession(table.id);
            start(newSession.id, table.light);
        } else if (activeSession.state === 'running') {
            console.log("Pausing session:", activeSession.id);
            pause(activeSession.id, table.light);
        } else if (activeSession.state === 'paused') {
            console.log("Resuming session:", activeSession.id);
            resume(activeSession.id, table.light);
        }
        // Here you would trigger the API sync call
        // syncAllSessions(); 
    };

    const handleStop = () => {
        if (!activeSession) return;
        if (window.confirm("Are you sure you want to end this session?")) {
            stop(activeSession.id, table.light);
            // Here you would trigger the API sync call for billing
            // toast.info(`Final time: ...`);
        }
    };

    const handleMoveTable = (newTableId: number) => {
        if (!activeSession) return;
        if (window.confirm(`Move session from ${table.name} to another table?`)) {
            moveTable(activeSession.id, newTableId);
            // syncAllSessions();
        }
    };

    const handleNameChange = async () => {
        // This logic remains as it's not part of the stopwatch state
        // You would call your API service here
    };

    const { hours, minutes, seconds } = displayTime;
    const isRunning = activeSession?.state === 'running';

    return (
        <div className="dark:bg-neutral-800 bg-neutral-100 w-11/12 mx-auto sm:w-full flex flex-col items-center p-4 rounded-lg shadow flex-shrink-0">
            {/* Name and Edit UI (mostly unchanged) */}
            <div className="flex items-center gap-3">
                {!editName ? (
                    <h1 className="dark:text-white font-medium text-xl">{table.name}</h1>
                ) : (
                    <input
                        ref={nameChangeRef}
                        defaultValue={table.name}
                        onKeyDown={(e) => { e.key === "Enter" && handleNameChange() }}
                        className={`focus:ring-0 bg-neutral-200 dark:bg-neutral-900 rounded-lg outline-none text-xl font-medium px-2`}
                    />
                )}
                {/* Edit/Check/X icons logic remains here */}
            </div>

            {/* Timer Display */}
            <div className="w-11/12 flex justify-center mt-8 bg-neutral-200 dark:bg-neutral-900 dark:text-neutral-300 text-neutral-600 rounded-lg py-8 text-5xl font-semibold">
                <p>{padWithLeadingZeros(hours, 2)}:{padWithLeadingZeros(minutes, 2)}:{padWithLeadingZeros(seconds, 2)}</p>
            </div>

            {/* Control Buttons */}
            <div className="w-11/12 flex justify-between gap-2 mt-4">
                <button
                    disabled={isLoading}
                    onClick={handleStartPauseResume}
                    className={`w-1/2 text-neutral-100 dark:text-neutral-900 transition-colors py-2 rounded-lg flex items-center gap-2 justify-center font-medium ${isRunning ? 'bg-warning hover:bg-yellow-400' : 'bg-success hover:bg-green-500'}`}
                >
                    {isRunning ? <PauseIcon /> : <StartIcon />}
                    {isRunning ? 'Pause' : (activeSession ? 'Resume' : 'Start')}
                </button>
                <button
                    disabled={!activeSession || isLoading}
                    onClick={handleStop}
                    className="w-1/2 disabled:opacity-50 disabled:hover:bg-red-700 bg-danger text-neutral-100 dark:text-neutral-900 hover:bg-red-600 transition-colors py-2 rounded-lg flex items-center gap-2 justify-center font-medium"
                >
                    <StopIcon />
                    End
                </button>
                {activeSession && !isRunning && (
                    <div className="dropdown dropdown-end w-1/2">
                        <label
                            tabIndex={0}
                            className="w-full cursor-pointer bg-neutral-300 hover:bg-neutral-400 dark:hover:bg-neutral-800 transition-colors dark:bg-neutral-900 py-2 rounded-lg flex items-center gap-2 justify-center font-medium"
                        >
                            <MoveIcon />
                            Move
                        </label>
                        <ul
                            tabIndex={0}
                            className="dropdown-content z-[1] menu p-2 shadow bg-base-100 dark:bg-neutral-900 rounded-box w-52"
                        >
                            {allTables.map((e) => {
                                if (e.id === table.id) return;
                                return (
                                    <li key={e.id}>
                                        <button
                                            onClick={() => {
                                                handleMoveTable(e.id);
                                            }}
                                            className="hover:bg-neutral-700 hover:text-neutral-200"
                                        >
                                            {e.name}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                )}
            </div>
        </div>
    );
};

export default V2Stopwatch;
